import type { CSSProperties } from 'react';

import {
  AppIconComponents,
  LanguageSwitchIconComponents,
  type AppIconName,
  type LanguageSwitchIconName,
  type SvgIconComponent,
} from '@/config/appIcons';

import styles from './SvgAsset.module.css';

/**
 * Перенос `lib/src/presentation/widgets/svg_asset.dart`.
 *
 * В Dart это `SvgPicture.asset(path, fit:, width:, height:, colorFilter:)`.
 * Путь к ассету в вебе не нужен: иконки уже разобраны в React-компоненты
 * (`@/config/appIcons`), поэтому вместо `path` принимается имя иконки —
 * вызывающий код в Dart писал `AppIcons.mic`, здесь это `'mic'` — или сам
 * компонент.
 *
 * Перекраска (`BlendMode.srcIn`) включается только когда задан `color`:
 * в Dart без цвета уходит `colorFilter: null`, и логотип сохраняет
 * собственные цвета.
 */

/** Подмножество `BoxFit`, которое выражается через `preserveAspectRatio`. */
export type SvgAssetFit = 'contain' | 'cover' | 'fill';

export interface SvgAssetProps {
  /** Имя иконки из `AppIcons`/`LanguageSwitchIcons` либо сам SVG-компонент. */
  readonly icon: AppIconName | LanguageSwitchIconName | SvgIconComponent;
  /** Число — пиксели, строка — готовое CSS-значение, например `wv(30)`. */
  readonly height?: number | string;
  readonly width?: number | string;
  readonly color?: string;
  readonly fit?: SvgAssetFit;
  readonly className?: string;
}

/** `BoxFit` → `preserveAspectRatio`: у inline-SVG за вписывание отвечает он. */
const PRESERVE_ASPECT_RATIO: Readonly<Record<SvgAssetFit, string>> = {
  contain: 'xMidYMid meet',
  cover: 'xMidYMid slice',
  fill: 'none',
};

function resolveIcon(icon: SvgAssetProps['icon']): SvgIconComponent | null {
  if (typeof icon !== 'string') return icon;
  if (icon in AppIconComponents) return AppIconComponents[icon as AppIconName];
  if (icon in LanguageSwitchIconComponents) {
    return LanguageSwitchIconComponents[icon as LanguageSwitchIconName];
  }
  return null;
}

function cssSize(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

/**
 * Размеры картинки. Задана одна сторона — вторая считается из `viewBox`,
 * как во Flutter, где `FittedBox` сохраняет пропорции рисунка.
 */
function sizeStyle(
  height: number | string | undefined,
  width: number | string | undefined,
): CSSProperties {
  const cssHeight = cssSize(height);
  const cssWidth = cssSize(width);
  if (cssHeight === undefined && cssWidth === undefined) return {};
  return { height: cssHeight ?? 'auto', width: cssWidth ?? 'auto' };
}

export function SvgAsset({
  icon,
  height,
  width,
  color,
  fit = 'contain',
  className,
}: SvgAssetProps) {
  const Icon = resolveIcon(icon);
  if (Icon === null) {
    console.error(`SvgAsset: неизвестное имя иконки — "${String(icon)}"`);
    return null;
  }
  const colored = color === undefined ? '' : styles.colored;
  const style: CSSProperties = {
    ...sizeStyle(height, width),
    ...(color === undefined ? {} : { color }),
  };
  return (
    <Icon
      className={`${styles.icon} ${colored} ${className ?? ''}`}
      style={style}
      preserveAspectRatio={PRESERVE_ASPECT_RATIO[fit]}
    />
  );
}
